/**
 * O acesso à API, num lugar só.
 *
 * Antes cada página montava o próprio `fetch`, e com isso repetia — ou esquecia
 * — três coisas que precisam ser iguais em toda chamada: mandar o cookie de
 * sessão, entender o formato de erro do servidor, e transformar esse erro em
 * algo que a tela consiga mostrar. Esquecer a terceira é o que produz aquele
 * "[object Object]" no lugar da mensagem.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    // Sem isto o cookie de sessão não viaja e tudo responde 401 — de um jeito
    // que parece problema de permissão e é problema de configuração.
    credentials: 'include',
    ...init,
  });

  const text = await response.text();
  let payload: unknown = null;
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const body = (payload ?? {}) as ApiErrorBody;
    throw new ApiError(
      response.status,
      body.error?.code ?? 'erro_desconhecido',
      body.error?.message ?? `A requisição falhou (HTTP ${response.status}).`,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),

  /**
   * Envio de arquivo com barra de progresso.
   *
   * XHR, e não `fetch`: um vídeo de cutscene tem dezenas de megabytes, e o
   * `fetch` não sabe dizer quanto já subiu. Sem a barra, o envio é
   * indistinguível de uma tela travada.
   */
  upload: <T>(path: string, file: File, onProgress?: (percent: number) => void): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const form = new FormData();
      form.append('arquivo', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', path);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
      };

      xhr.onload = () => {
        let payload: unknown = null;
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          payload = null;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(payload as T);
        } else {
          const body = (payload ?? {}) as ApiErrorBody;
          reject(new ApiError(
            xhr.status,
            body.error?.code ?? 'erro_desconhecido',
            body.error?.message ?? `O envio falhou (HTTP ${xhr.status}).`,
          ));
        }
      };

      xhr.onerror = () => reject(new ApiError(0, 'conexao', 'A conexão caiu durante o envio.'));
      xhr.send(form);
    }),
};

/** A mensagem de um erro, seja ele da API ou do próprio navegador. */
export function messageOf(error: unknown, fallback = 'Algo deu errado.'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}
